// AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { loginUser, logoutUser, registerUser, getUserData, updateUserProfile } from '../api/auth';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeDoc = null;

    // Listen to authentication state changes
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clean up previous document listener if user logs out or changes
      if (unsubscribeDoc) {
        unsubscribeDoc();
        unsubscribeDoc = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);
        
        // Listen to user document in Firestore in real-time
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        unsubscribeDoc = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          } else {
            setUserData(null);
          }
          setLoading(false);
        }, (error) => {
          console.error('Error listening to user data in context:', error);
          setLoading(false);
        });
      } else {
        setUser(null);
        setUserData(null);
        setLoading(false);
      }
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeAuth();
      if (unsubscribeDoc) {
        unsubscribeDoc();
      }
    };
  }, []);

  const login = async (email, password) => {
    try {
      const result = await loginUser(email, password);
      setUser(result.user);
      setUserData(result.userData);
      return result;
    } catch (error) {
      console.error('Login error in context:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
      setUser(null);
      setUserData(null);
    } catch (error) {
      console.error('Logout error in context:', error);
      throw error;
    }
  };

  const register = async (email, password, userData) => {
    try {
      const result = await registerUser(email, password, userData);
      setUser(result.user);
      setUserData(result.userData);
      return result;
    } catch (error) {
      console.error('Registration error in context:', error);
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }
      await updateUserProfile(user.uid, updates);
      // Update local state
      setUserData((prevData) => ({
        ...prevData,
        ...updates,
      }));
    } catch (error) {
      console.error('Update profile error in context:', error);
      throw error;
    }
  };

  const refreshUserData = async () => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }
      const data = await getUserData(user.uid);
      setUserData(data);
      return data;
    } catch (error) {
      console.error('Refresh user data error:', error);
      throw error;
    }
  };

  const value = {
    user,
    userData,
    loading,
    login,
    logout,
    register,
    updateProfile,
    refreshUserData,
    isAuthenticated: !!user,
    isAdmin: userData?.role === 'admin',
    isMember: userData?.role === 'member',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
